import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import API_BASE_URL from '../config/api';
import { useAuth } from '../context/AuthContext';

function PaymentFailed() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const txnid = searchParams.get('txnid');
  const [orderStatus, setOrderStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  // Check if the order is actually confirmed despite showing as failed
  useEffect(() => {
    const checkOrderStatus = async () => {
      try {
        if (!txnid) {
          setIsChecking(false);
          return;
        }

        console.log('🔍 Checking order status with txnid:', txnid);

        // Try to find order by transaction ID
        const response = await fetch(`${API_BASE_URL}/api/orders/admin/all-orders`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const orders = data.data || [];
          
          // Find order with matching payuPaymentId (mihpayid) or paymentId (txnid)
          const foundOrder = orders.find(o => 
            (o.payuPaymentId && o.payuPaymentId.includes(txnid)) ||
            (o.paymentId && o.paymentId.includes(txnid))
          );

          if (foundOrder) {
            console.log('✅ Found order:', { id: foundOrder.id, status: foundOrder.status });
            setOrderStatus(foundOrder.status);

            // If order is confirmed, redirect to success page
            if (foundOrder.status === 'confirmed') {
              console.log('✅ Order is confirmed despite redirect to failed page!');
              setTimeout(() => {
                navigate(`/payment-success?txnid=${encodeURIComponent(txnid)}&orderId=${encodeURIComponent(foundOrder.id)}`);
              }, 2000);
            }
          } else {
            console.log('⚠️ Order not found with txnid:', txnid);
          }
        }
      } catch (err) {
        console.error('Error checking order:', err);
      } finally {
        setIsChecking(false);
      }
    };

    if (token) {
      checkOrderStatus();
    } else {
      setIsChecking(false);
    }
  }, [txnid, token, navigate]);

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'hash_mismatch':
        return 'Payment verification encountered a technical issue. Your payment may still have been processed. Please check your orders.';
      case 'payment_pending':
        return 'Your payment is still being processed. This can take a few minutes. Please check your orders or contact support.';
      case 'payment_failed':
        return 'Your payment could not be processed. Please try again with a different payment method. If amount was deducted, it will be refunded within 3-5 business days.';
      case 'server_error':
        return 'A server error occurred while processing your payment. Please try again or contact support if the problem persists.';
      default:
        return errorCode ? `Error: ${errorCode.replace(/_/g, ' ')}` : 'Your payment could not be completed. Please try again.';
    }
  };

  const isPending = error === 'payment_pending';
  const isHashMismatch = error === 'hash_mismatch';
  const headerIcon = isPending ? '⏳' : isHashMismatch ? '⚠️' : '❌';
  const headerColor = isPending ? colors.warning?.[600] || '#d97706' : isHashMismatch ? colors.warning?.[600] || '#d97706' : colors.error;
  const headerTitle = isPending ? 'Payment Pending' : isHashMismatch ? 'Verification Issue' : 'Payment Failed';
  const bodyMessage = getErrorMessage(error);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: spacing.xl }}>
      <motion.div
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          padding: spacing.xl,
          boxShadow: shadows.lg,
          maxWidth: '420px',
          width: '100%',
          textAlign: 'center',
        }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {isChecking && (
          <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <div style={{ fontSize: '3rem', marginBottom: spacing.lg }}>🔍</div>
            <p style={{ fontSize: '1rem', color: colors.text.secondary }}>Verifying your payment status...</p>
          </motion.div>
        )}

        {!isChecking && orderStatus === 'confirmed' && (
          <>
            <motion.div
              style={{ fontSize: '3rem', marginBottom: spacing.lg }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              🎉
            </motion.div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.success, margin: 0, marginBottom: spacing.sm }}>
              Payment Confirmed!
            </h2>
            <p style={{ fontSize: '0.95rem', color: colors.text.secondary, marginBottom: spacing.lg }}>
              Your payment was successfully processed. Redirecting to your order...
            </p>
          </>
        )}

        {!isChecking && orderStatus !== 'confirmed' && (
          <>
            <motion.div
              style={{ fontSize: '4rem', marginBottom: spacing.lg, color: headerColor }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
            >
              {headerIcon}
            </motion.div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 900, color: headerColor, margin: 0, marginBottom: spacing.sm }}>
              {headerTitle}
            </h2>
            <p style={{ fontSize: '1rem', color: colors.text.secondary, marginBottom: spacing.lg }}>
              {bodyMessage}
            </p>

            {txnid && (
              <div style={{ padding: spacing.md, backgroundColor: isPending || isHashMismatch ? '#fef9c3' : '#fef2f2', borderRadius: '0.5rem', marginBottom: spacing.lg }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: isPending || isHashMismatch ? '#92400e' : '#991b1b' }}>
                  <strong>Transaction ID:</strong> {txnid}
                </p>
                <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.85rem', color: isPending || isHashMismatch ? '#78350f' : '#b91c1c' }}>
                  {isPending 
                    ? 'Your transaction is still being processed. Please wait and check your order status again.' 
                    : isHashMismatch
                    ? 'Your payment may still have been processed. Check your orders below.'
                    : 'Please contact support if amount was deducted.'}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <motion.button
                onClick={() => navigate('/orders')}
                style={{
                  width: '100%',
                  padding: spacing.lg,
                  backgroundColor: colors.primary[600],
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
                whileHover={{ backgroundColor: colors.primary[700] }}
                whileTap={{ scale: 0.95 }}
              >
                ✅ Check My Orders
              </motion.button>
              <motion.button
                onClick={() => navigate('/checkout')}
                style={{
                  width: '100%',
                  padding: spacing.lg,
                  backgroundColor: colors.primary[600],
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
                whileHover={{ backgroundColor: colors.primary[700] }}
                whileTap={{ scale: 0.95 }}
              >
                {isPending ? '⏳ Check Status' : '🔄 Try Again'}
              </motion.button>
              <motion.button
                onClick={() => navigate('/cart')}
                style={{
                  width: '100%',
                  padding: spacing.lg,
                  backgroundColor: colors.neutral?.[200] || '#e5e7eb',
                  color: colors.text.primary,
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
                whileHover={{ backgroundColor: colors.neutral?.[300] || '#d1d5db' }}
                whileTap={{ scale: 0.95 }}
              >
                🛒 Return to Cart
              </motion.button>
            </div>

            <p style={{ fontSize: '0.8rem', color: colors.text.secondary, marginTop: spacing.lg }}>
              Need help? <a href="mailto:support@hcsent.com" style={{ color: colors.primary[600], textDecoration: 'none' }}>Contact Support</a>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default PaymentFailed;
